import React, { useState, useEffect, useMemo } from "react";
import { BasePage } from "components/BasePage/BasePage";
import { JsonEditor } from "components/inputs/JsonEditor";
import { useClient } from "utils/client";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ControlContext } from "context/Controls";
import { isDev, isLocal } from "context/FeatureFlag";
import { format } from "date-fns";
import { Plan, PlanBadge } from "pages/Backoffice/TEST/PlanSelection";

// Define the User type based on the API response
interface User {
  sub: string;
  email: string;
  accountId: string;
}

// Define the UserActivity type based on the actual API response
interface UserActivity {
  userId: string;
  email: string;
  accountId?: string;
}

// Account management types
interface AccountSubscription {
  plan: string;
  frequency: string;
  availableSeats: number;
  freeSeats: number;
  cancelled: boolean;
  paid: boolean;
  trailEnd: number;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
}

interface AccountMember {
  userId: string;
  email: string;
  role: string;
}

interface AccountConfig {
  resourceId: string;
  emailAddress: string;
  payerUser: string;
  subscription: AccountSubscription;
  members: AccountMember[];
  memberCount: number;
}

export const FeatureFlagsBackOffice = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { displayNotification } = React.useContext(ControlContext);

  const [error, setError] = useState<string | null>(null);

  // Fetch feature flags
  const { data: featureFlags, isLoading: isLoadingFlags } = useQuery(
    "featureflags",
    async () => {
      try {
        const res = await client.get("/featureflags");
        return res.data;
      } catch (err) {
        setError("Failed to load feature flags");
        throw err;
      }
    },
  );

  // Update feature flags
  const { mutate: updateFeatureFlags, isLoading: isSaving } = useMutation(
    async (updatedFlags: any) => {
      try {
        const res = await client.post("/featureflags", updatedFlags);
        return res.data;
      } catch (err) {
        setError("Failed to update feature flags");
        throw err;
      }
    },
    {
      onSuccess: () => {
        // Invalidate and refetch the feature flags query
        queryClient.invalidateQueries("featureflags");
        displayNotification!("Feature flags updated", "success");
        setError(null);
      },
    },
  );

  const handleSave = (updatedFlags: any) => {
    updateFeatureFlags(updatedFlags);
  };

  // Fetch users from the /cognito endpoint
  const { data: users, isLoading: isLoadingUsers } = useQuery(
    "cognito-users",
    async () => {
      try {
        const res = await client.get("/cognito");
        return res.data as User[];
      } catch (err) {
        setError("Failed to load users");
        throw err;
      }
    },
  );

  // State for search input and filtered users
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // State for activity date range
  const [startDate, setStartDate] = useState<string>(
    format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"), // Default to 7 days ago
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd"), // Default to today
  );

  // Filter users based on search term
  useEffect(() => {
    if (!users) {
      setFilteredUsers([]);
      return;
    }

    if (searchTerm.trim() === "") {
      setFilteredUsers(users);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.email.toLowerCase().includes(lowerSearchTerm) ||
        user.sub.toLowerCase().includes(lowerSearchTerm) ||
        user.accountId.toLowerCase().includes(lowerSearchTerm),
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle user selection
  const handleUserSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = e.target.value;
    if (!userId) {
      setSelectedUser(null);
      return;
    }

    const user = users?.find((u) => u.sub === userId) || null;
    setSelectedUser(user);
  };

  // Fetch user activity data
  const { data: userActivity, isLoading: isLoadingActivity } = useQuery(
    ["cognito-activity", startDate, endDate],
    async () => {
      try {
        const res = await client.get(
          `/cognito/activity?startDate=${startDate}&endDate=${endDate}`,
        );
        return res.data as UserActivity[];
      } catch (err) {
        setError("Failed to load user activity");
        throw err;
      }
    },
    {
      enabled: !!startDate && !!endDate,
      refetchOnWindowFocus: false,
    },
  );

  // Activity plan filter
  const [activityPlanFilter, setActivityPlanFilter] = useState<string>("");

  // Derive unique accountIds from the activity list by joining with users
  const uniqueActivityAccountIds = useMemo(() => {
    if (!userActivity) return [];
    const ids = userActivity.map((activity) => {
      if (activity.accountId) return activity.accountId;
      return users?.find((u) => u.sub === activity.userId)?.accountId;
    });
    return [...new Set(ids.filter((id): id is string => !!id))];
  }, [userActivity, users]);

  // Fetch account configs for all accounts in the activity list
  const { data: activityAccountConfigs, isLoading: isLoadingActivityAccounts } =
    useQuery(
      ["activity-account-configs", uniqueActivityAccountIds],
      async () => {
        const configs = await Promise.all(
          uniqueActivityAccountIds.map((accountId) =>
            client
              .get(`/admin/accounts?accountId=${accountId}`)
              .then((r) => r.data as AccountConfig),
          ),
        );
        return uniqueActivityAccountIds.reduce<Record<string, AccountConfig>>(
          (map, accountId, i) => {
            map[accountId] = configs[i];
            return map;
          },
          {},
        );
      },
      {
        enabled: uniqueActivityAccountIds.length > 0,
        refetchOnWindowFocus: false,
      },
    );

  // Enrich activity rows with account info and apply plan filter
  const nowSeconds = Date.now() / 1000;
  const enrichedActivity = useMemo(() => {
    if (!userActivity) return [];
    return userActivity
      .map((activity) => {
        const accountId =
          activity.accountId ||
          users?.find((u) => u.sub === activity.userId)?.accountId;
        const config = accountId ? activityAccountConfigs?.[accountId] : undefined;
        const plan = config?.subscription?.plan;
        const trailEnd = config?.subscription?.trailEnd;
        const trialExpired =
          trailEnd !== undefined && trailEnd !== 0
            ? trailEnd < nowSeconds
            : undefined;
        return { ...activity, accountId, plan, trailEnd, trialExpired };
      })
      .filter((row) => !activityPlanFilter || row.plan === activityPlanFilter);
  }, [userActivity, users, activityAccountConfigs, activityPlanFilter, nowSeconds]);

  const handleActivityAccountClick = (accountId: string) => {
    const user = users?.find((u) => u.accountId === accountId) || null;
    setSelectedUser(user);
  };

  // Account management state
  const [isEditingSubscription, setIsEditingSubscription] = useState(false);
  const [editPlan, setEditPlan] = useState<string>("");
  const [editSeats, setEditSeats] = useState<number>(0);
  const [editTrailEnd, setEditTrailEnd] = useState<string>("");

  // Fetch account configuration when a user is selected
  const {
    data: accountConfig,
    isLoading: isLoadingAccount,
    isError: isAccountError,
  } = useQuery(
    ["admin-account", selectedUser?.accountId],
    async () => {
      const res = await client.get(
        `/admin/accounts?accountId=${selectedUser!.accountId}`,
      );
      return res.data as AccountConfig;
    },
    {
      enabled: !!selectedUser?.accountId,
      refetchOnWindowFocus: false,
      onError: () => {
        setError("Failed to load account configuration");
      },
    },
  );

  // Update subscription mutation
  const { mutate: updateSubscription, isLoading: isUpdatingSubscription } =
    useMutation(
      async (payload: {
        accountId: string;
        plan: string;
        availableSeats: number;
        trailEnd: number;
      }) => {
        const res = await client.put("/admin/accounts/subscription", payload);
        return res.data;
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            "admin-account",
            selectedUser?.accountId,
          ]);
          displayNotification!("Subscription updated successfully", "success");
          setIsEditingSubscription(false);
          setError(null);
        },
        onError: () => {
          setError("Failed to update subscription");
        },
      },
    );

  const trailEndToInput = (trailEnd: number) =>
    trailEnd ? format(new Date(trailEnd * 1000), "yyyy-MM-dd'T'HH:mm") : "";

  const inputToTrailEnd = (value: string) =>
    value ? Math.floor(new Date(value).getTime() / 1000) : 0;

  // Reset edit mode when user changes
  useEffect(() => {
    setIsEditingSubscription(false);
    if (accountConfig?.subscription) {
      setEditPlan(accountConfig.subscription.plan);
      setEditSeats(accountConfig.subscription.availableSeats);
      setEditTrailEnd(trailEndToInput(accountConfig.subscription.trailEnd));
    }
  }, [accountConfig, selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enter edit mode
  const handleEditSubscription = () => {
    if (accountConfig?.subscription) {
      setEditPlan(accountConfig.subscription.plan);
      setEditSeats(accountConfig.subscription.availableSeats);
      setEditTrailEnd(trailEndToInput(accountConfig.subscription.trailEnd));
    }
    setIsEditingSubscription(true);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setIsEditingSubscription(false);
    if (accountConfig?.subscription) {
      setEditPlan(accountConfig.subscription.plan);
      setEditSeats(accountConfig.subscription.availableSeats);
      setEditTrailEnd(trailEndToInput(accountConfig.subscription.trailEnd));
    }
  };

  // Save subscription changes
  const handleSaveSubscription = () => {
    if (!selectedUser?.accountId || editSeats < 0) return;
    updateSubscription({
      accountId: selectedUser.accountId,
      plan: editPlan,
      availableSeats: editSeats,
      trailEnd: inputToTrailEnd(editTrailEnd),
    });
  };

  // Handle date changes
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  return (
    <BasePage
      breadcrumbItems={[
        { label: "Backoffice", location: "/_backoffice" },
        { label: "Feature Flags", location: "/_backoffice/_featureflags" },
      ]}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          Feature Flags Configuration{" "}
          <span className="text-red-600">DANGER ZONE!</span>
        </h1>

        <p className="text-sm text-gray-500 mb-4">
          <span className="text-red-500 font-bold">
            Du befindest dich auf der <b>{isDev || isLocal ? "DEV" : "PROD"}</b>
            -Umgebung
          </span>
        </p>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Cognito Users
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Du kannst alle User im Certain System suchen. Für die Feature Flags
            benötigst du die "ID". Dazu wählst du entweder einen User direkt aus
            der Liste <b>oder</b> du gibst erst einen Filterbegriff ein.{" "}
            <b>Beachte, dass die Emails maskiert sind!</b>
          </p>

          {isLoadingUsers ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading users...</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label
                    htmlFor="user-search"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Search Users
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="user-search"
                      id="user-search"
                      className="focus:ring-cyan-500 focus:border-cyan-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Search by email or ID"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="user-select"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Select User
                  </label>
                  <select
                    id="user-select"
                    name="user-select"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                    value={selectedUser?.sub || ""}
                    onChange={handleUserSelect}
                  >
                    <option value="">Select a user</option>
                    {filteredUsers.map((user) => (
                      <option key={user.sub} value={user.sub}>
                        {user.email} ({user.accountId} | {user.sub})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedUser && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-md">
                    <h3 className="text-lg font-medium text-gray-900">
                      Selected User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Email:</span>{" "}
                        {selectedUser.email}
                      </p>
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Sub:</span>{" "}
                        {selectedUser.sub}
                      </p>
                      <p className="text-sm text-gray-500">
                        <span className="font-medium text-cyan-600">
                          Account-ID:
                        </span>{" "}
                        {selectedUser.accountId}
                      </p>
                    </div>
                  </div>
                )}

                {/* Account Configuration Section */}
                {selectedUser && (
                  <div className="mt-6">
                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-medium text-gray-900">
                          Account Configuration
                        </h3>
                        {accountConfig && !isEditingSubscription && (
                          <button
                            type="button"
                            onClick={handleEditSubscription}
                            className="inline-flex items-center px-3 py-1.5 border border-cyan-600 text-sm font-medium rounded-md text-cyan-600 bg-white hover:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                          >
                            Edit Subscription
                          </button>
                        )}
                      </div>

                      {isLoadingAccount && (
                        <div className="text-center py-8">
                          <p className="text-gray-500">
                            Loading account configuration...
                          </p>
                        </div>
                      )}

                      {isAccountError && !isLoadingAccount && (
                        <div className="text-center py-8 bg-red-50 rounded-md">
                          <p className="text-red-500">
                            Failed to load account configuration.
                          </p>
                        </div>
                      )}

                      {accountConfig && !isLoadingAccount && (
                        <div className="space-y-6">
                          {/* Account Info */}
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Resource ID
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900 font-mono">
                                {accountConfig.resourceId}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Email Address
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {accountConfig.emailAddress}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Payer User
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900 font-mono">
                                {accountConfig.payerUser}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm font-medium text-gray-500">
                                Member Count
                              </dt>
                              <dd className="mt-1 text-sm text-gray-900">
                                {accountConfig.memberCount}
                              </dd>
                            </div>
                          </div>

                          {/* Subscription Details */}
                          <div className="border-t border-gray-200 pt-4">
                            <h4 className="text-md font-medium text-gray-900 mb-3">
                              Subscription
                            </h4>

                            {isEditingSubscription ? (
                              /* Edit Mode */
                              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                <p className="text-sm text-yellow-700 mb-4 font-medium">
                                  ⚠️ Editing subscription — changes will be
                                  applied immediately.
                                </p>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                  <div>
                                    <label
                                      htmlFor="edit-plan"
                                      className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                      Plan
                                    </label>
                                    <select
                                      id="edit-plan"
                                      value={editPlan}
                                      onChange={(e) =>
                                        setEditPlan(e.target.value)
                                      }
                                      className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                                    >
                                      {Object.values(Plan).map((plan) => (
                                        <option key={plan} value={plan}>
                                          {plan}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label
                                      htmlFor="edit-seats"
                                      className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                      Available Seats
                                    </label>
                                    <input
                                      type="number"
                                      id="edit-seats"
                                      min={0}
                                      value={editSeats}
                                      onChange={(e) =>
                                        setEditSeats(
                                          parseInt(e.target.value, 10) || 0,
                                        )
                                      }
                                      className="block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                                    />
                                  </div>
                                  <div>
                                    <label
                                      htmlFor="edit-trail-end"
                                      className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                      Trial End
                                    </label>
                                    <input
                                      type="datetime-local"
                                      id="edit-trail-end"
                                      value={editTrailEnd}
                                      onChange={(e) =>
                                        setEditTrailEnd(e.target.value)
                                      }
                                      className="block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                                    />
                                  </div>
                                </div>
                                <div className="mt-4 flex gap-3">
                                  <button
                                    type="button"
                                    onClick={handleSaveSubscription}
                                    disabled={isUpdatingSubscription}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isUpdatingSubscription
                                      ? "Saving..."
                                      : "Save Changes"}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    disabled={isUpdatingSubscription}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* Read-only Mode */
                              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Plan
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900">
                                    <PlanBadge
                                      plan={
                                        accountConfig.subscription.plan as Plan
                                      }
                                    />
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Frequency
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900">
                                    {accountConfig.subscription.frequency}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Available Seats
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900">
                                    {accountConfig.subscription.availableSeats}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Free Seats
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900">
                                    {accountConfig.subscription.freeSeats}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Paid
                                  </dt>
                                  <dd className="mt-1 text-sm">
                                    <span
                                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                        accountConfig.subscription.paid
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {accountConfig.subscription.paid
                                        ? "Yes"
                                        : "No"}
                                    </span>
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Cancelled
                                  </dt>
                                  <dd className="mt-1 text-sm">
                                    <span
                                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                        accountConfig.subscription.cancelled
                                          ? "bg-red-100 text-red-800"
                                          : "bg-green-100 text-green-800"
                                      }`}
                                    >
                                      {accountConfig.subscription.cancelled
                                        ? "Yes"
                                        : "No"}
                                    </span>
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Trial End
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900">
                                    {accountConfig.subscription.trailEnd
                                      ? format(
                                          new Date(
                                            accountConfig.subscription
                                              .trailEnd * 1000,
                                          ),
                                          "dd.MM.yyyy HH:mm",
                                        )
                                      : "—"}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Stripe Customer ID
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900 font-mono truncate">
                                    {accountConfig.subscription
                                      .stripeCustomerId || "—"}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="text-sm font-medium text-gray-500">
                                    Stripe Subscription ID
                                  </dt>
                                  <dd className="mt-1 text-sm text-gray-900 font-mono truncate">
                                    {accountConfig.subscription
                                      .stripeSubscriptionId || "—"}
                                  </dd>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Members Table */}
                          {accountConfig.members &&
                            accountConfig.members.length > 0 && (
                              <div className="border-t border-gray-200 pt-4">
                                <h4 className="text-md font-medium text-gray-900 mb-3">
                                  Members ({accountConfig.memberCount})
                                </h4>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th
                                          scope="col"
                                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                          User ID
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                          Email
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                          Role
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {accountConfig.members.map((member) => (
                                        <tr key={member.userId}>
                                          <td className="px-4 py-2 whitespace-nowrap text-sm font-mono text-gray-900">
                                            {member.userId}
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                            {member.email}
                                          </td>
                                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                                            <span
                                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                member.role === "admin"
                                                  ? "bg-purple-100 text-purple-800"
                                                  : "bg-gray-100 text-gray-800"
                                              }`}
                                            >
                                              {member.role}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            User Activity
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            View the latest user activity on the platform within a selected date
            range. This shows masked email addresses of users who have been
            active during the specified period.
          </p>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-4">
                <div>
                  <label
                    htmlFor="start-date"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="start-date"
                    name="start-date"
                    className="focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={startDate}
                    onChange={handleStartDateChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="end-date"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    End Date
                  </label>
                  <input
                    type="date"
                    id="end-date"
                    name="end-date"
                    className="focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={endDate}
                    onChange={handleEndDateChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="activity-plan-filter"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Filter by Plan
                  </label>
                  <select
                    id="activity-plan-filter"
                    className="focus:ring-cyan-500 focus:border-cyan-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={activityPlanFilter}
                    onChange={(e) => setActivityPlanFilter(e.target.value)}
                  >
                    <option value="">All Plans</option>
                    {Object.values(Plan).map((plan) => (
                      <option key={plan} value={plan}>
                        {plan}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isLoadingActivity ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Loading activity data...</p>
                </div>
              ) : userActivity && userActivity.length > 0 ? (
                <div className="mt-4 overflow-x-auto">
                  {isLoadingActivityAccounts && (
                    <p className="text-sm text-gray-400 mb-2">
                      Loading account info...
                    </p>
                  )}
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          User ID
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Email (Masked)
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Account ID
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Plan
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Trial Expired
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {enrichedActivity.map((activity, index) => (
                        <tr key={`${activity.userId}-${index}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                            {activity.userId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {activity.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                            {activity.accountId ? (
                              <button
                                type="button"
                                onClick={() =>
                                  handleActivityAccountClick(activity.accountId!)
                                }
                                className="text-cyan-600 hover:text-cyan-800 hover:underline"
                              >
                                {activity.accountId}
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {activity.plan &&
                            (Object.values(Plan) as string[]).includes(
                              activity.plan,
                            ) ? (
                              <PlanBadge plan={activity.plan as Plan} />
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {activity.trailEnd === undefined ? (
                              <span className="text-gray-400">—</span>
                            ) : activity.trailEnd === 0 ? (
                              <span className="text-gray-400">not set</span>
                            ) : (
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                  activity.trialExpired
                                    ? "bg-red-100 text-red-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                              >
                                {activity.trialExpired ? "Yes" : "No"}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {enrichedActivity.length === 0 && (
                    <div className="text-center py-8 bg-gray-50 rounded-md mt-2">
                      <p className="text-gray-500">
                        No activity matches the selected filter.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-md">
                  <p className="text-gray-500">
                    No activity data found for the selected date range.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {isLoadingFlags ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading feature flags...</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <p className="text-sm text-gray-500 mb-4">
                Solltest du dir unsicher sein, lass es besser!
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Die hier gespeicherten Änderungen werden die Feature-Flags{" "}
                <u>sofort</u> für die User dieser Umgebung verändern. D.h.
                Features werden sichtbar oder unsichtbar unmittelbar nach dem
                Speichern.
              </p>
              <JsonEditor
                defaultValue={featureFlags}
                onSave={handleSave}
                isLoading={isSaving}
              />
            </div>
          </div>
        )}
      </div>
    </BasePage>
  );
};
